import { useEffect, useRef, useState } from "react";
import { Download, ShieldCheck, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, errMsg } from "../../lib/api";
import { fmtBytes, fmtDateTime, label } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { Card, Empty, Td, Th } from "../common/UI";
import { Button } from "../ui/button";

const UPLOAD_PERMS = ["measure.edit", "measure.recommend", "measure.approve", "vote.record", "exec.record", "review.record", "measure.publish", "effectivity.verify"];

export default function DocumentsTab({ m, onSaved }) {
  const { can } = useAuth();
  const [docs, setDocs] = useState([]);
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({ document_type: "draft_measure", title: "", is_official: false });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const load = () => api.get(`/measures/${m.id}/documents`).then((r) => setDocs(r.data));
  useEffect(() => {
    load();
    api.get("/document-types").then((r) => setTypes(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m.id]);
  const upload = async (e) => {
    e.preventDefault();
    const file = fileRef.current.files[0];
    if (!file) return toast.error("Choose a file");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("document_type", form.document_type);
    fd.append("title", form.title || file.name);
    fd.append("is_official", form.is_official);
    setBusy(true);
    try {
      await api.post(`/measures/${m.id}/documents`, fd);
      toast.success("Document uploaded");
      fileRef.current.value = "";
      setForm({ ...form, title: "" });
      load();
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const dl = (d) => downloadFile(`/documents/${d.id}/download`, {}, d.filename)
    .then((ok) => toast[ok === "true" ? "success" : "warning"](ok === "true" ? "Downloaded · integrity verified" : "Downloaded · integrity check failed"))
    .catch((e) => toast.error(errMsg(e)));
  return (
    <div className="space-y-4">
      {UPLOAD_PERMS.some(can) && (
        <Card title="Upload document" testId="document-upload">
          <form onSubmit={upload} className="grid gap-3 md:grid-cols-[180px_1fr_1fr_auto] md:items-end">
            <select className="otms-input" value={form.document_type} onChange={(e) => setForm({ ...form, document_type: e.target.value })} data-testid="document-type-select">
              {types.map((t) => <option key={t} value={t}>{label(t)}</option>)}
            </select>
            <input className="otms-input" placeholder="Title (optional)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="document-title-input" />
            <input ref={fileRef} type="file" accept=".pdf,.docx,.doc,.xlsx,.png,.jpg,.jpeg,.txt,.csv,.tif,.tiff" className="text-xs" data-testid="document-file-input" />
            <Button type="submit" size="sm" disabled={busy} className="bg-sky-600 hover:bg-sky-700" data-testid="document-upload-button"><Upload className="mr-1.5 h-4 w-4" />{busy ? "Uploading…" : "Upload"}</Button>
            <label className="flex items-center gap-2 text-xs text-slate-600 md:col-span-4">
              <input type="checkbox" checked={form.is_official} onChange={(e) => setForm({ ...form, is_official: e.target.checked })} data-testid="document-official-checkbox" />
              Mark as official / authenticated copy
            </label>
          </form>
          <p className="mt-2 text-[11px] text-slate-400">Files are stored in private object storage. Each upload becomes a new version; originals are never overwritten.</p>
        </Card>
      )}
      <div className="overflow-x-auto rounded-md border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[720px]" data-testid="documents-table">
          <thead><tr><Th>Document</Th><Th>Type</Th><Th>Ver.</Th><Th>Integrity</Th><Th>Uploaded</Th><Th /></tr></thead>
          <tbody>
            {docs.map((d) => (
              <tr key={d.id} className={d.is_superseded ? "opacity-60" : ""}>
                <Td><div className="font-medium text-slate-800">{d.title}</div><div className="text-[11px] text-slate-500">{d.filename} · {fmtBytes(d.size_bytes)}</div></Td>
                <Td>{label(d.document_type)}{d.is_official && <span className="ml-1 rounded bg-emerald-100 px-1 text-[10px] text-emerald-800">official</span>}{d.is_superseded && <span className="ml-1 text-[10px]">(superseded)</span>}</Td>
                <Td>v{d.version_no}</Td>
                <Td><span className="flex items-center gap-1 font-mono text-[10px] text-slate-500" title={d.sha256}><ShieldCheck className="h-3 w-3 text-emerald-600" />{d.sha256.slice(0, 12)}…</span></Td>
                <Td>{d.uploaded_by_name}<div className="text-[11px] text-slate-400">{fmtDateTime(d.created_at)}</div></Td>
                <Td><Button size="sm" variant="ghost" onClick={() => dl(d)} data-testid={`document-download-${d.id}`}><Download className="h-4 w-4" /></Button></Td>
              </tr>
            ))}
          </tbody>
        </table>
        {docs.length === 0 && <Empty text="No documents uploaded yet." />}
      </div>
    </div>
  );
}
